import { useCallback, useEffect } from 'react';
import { getHomeLoansDashboardImpressionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardImpressionEvent';
import { getHomeLoansDashboardInteractionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardInteractionEvent';
import {
    HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS,
    HOME_LOANS_DASHBOARD_INTERACTION_EVENTS,
} from 'src/analytics/events/registeredEvents';
import { trackEvent } from 'src/analytics/events/trackEvent';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import { ZHLPersonalizedNextStepState } from 'src/services/graphql/generated/graphql';

export type UpsellAnalytics = {
    datadogClient: DatadogRUMService.Client;
    trackPrimaryCTAClick: () => void;
    trackSecondaryCTAClick: () => void;
};

export const useUpsellAnalytics = ({
    lead_id,
    currentState,
    loading,
    isFirstPartyExperience,
}: {
    lead_id: string;
    currentState: ZHLPersonalizedNextStepState | undefined;
    loading: boolean;
    isFirstPartyExperience: boolean;
}): UpsellAnalytics => {
    const datadogClient = DatadogRUMService.Client.getInstance();
    useEffect(() => {
        if (loading) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardImpressionEvent({
                lead_id,
                // castable because should status be null after loading is false we'll have thrown a fatal error anyways
                journey_stage_cd: currentState as NonNullable<typeof currentState>,
                event: HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS.UPSELL_MODULE_LOADED,
            })
        );
    }, [lead_id, currentState, loading]);

    const trackPrimaryCTAClick = useCallback(() => {
        if (!currentState) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: currentState,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.PRIMARY_CTA_CLICK,
            })
        );
    }, [currentState, lead_id]);

    const trackSecondaryCTAClick = useCallback(() => {
        if (!currentState) {
            return;
        }
        if (isFirstPartyExperience) {
            // TODO (ZHLCX-0000): Create analytics events for this if needed, but we also use UTM params to track this
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: currentState,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.SECONDARY_CTA_CLICK,
            })
        );
    }, [lead_id, currentState, isFirstPartyExperience]);

    return {
        datadogClient,
        trackPrimaryCTAClick,
        trackSecondaryCTAClick,
    };
};
